import { useState, useRef } from 'react';
import type { VisualField } from '../../../types/wizardTypes';
import { useMappingBuilder } from '../hooks/useMappingBuilder';
import { useQueryMapBuilder } from '../hooks/useQueryMapBuilder';
import { useHeaderBuilder } from '../hooks/useHeaderBuilder';
import { schemaToFields, fieldsToSchema } from '../converters/fieldSchemaConverter';
import { safeJsonParse } from '../../../utils/security';
import MappingRow from './MappingRow';
import SqlQuerySection from './SqlQuerySection';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import TemplateEditor from '../../../components/templateEditor/TemplateEditor';
import AiGenerator from '../../../components/aiGenerator/AiGenerator';
import { AiGenerationTypes } from '../../../enums/aiGenerationTypes';
import InfoLabel from '../../../ui/InfoLabel/InfoLabel';
import { Plus, ChevronDown, ChevronUp, Sparkles, FileJson, Globe, Trash2, ListPlus, AlertTriangle, Database, Zap } from 'lucide-react';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { configApi, aiApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import styles from './RequestMappingStep.module.css';

/** Extract {{param}} names from the URL path only (before any ?query string) */
function extractPathParams(fullPath: string): string[] {
  const pathOnly = fullPath.split('?')[0];
  const matches = [...pathOnly.matchAll(/\{\{\s*([^}|]+?)\s*(?:\|[^}]*)?\}\}/g)];
  return [...new Set(matches.map(m => m[1].trim()))];
}

/**
 * Classify the caret/selection in a text input as a "Make Dynamic" or "Make Static" target.
 * A caret or selection that falls inside an existing {{ token }} always resolves to that whole
 * token as static-able - so double-clicking a word already wrapped in braces never offers to
 * wrap it again (which used to nest them: {{{{userId}}}}).
 * Returns null when neither action applies (e.g. a selection straddling a token boundary).
 */
function resolveDynamicSelection(
  value: string,
  selStart: number,
  selEnd: number
): { selStart: number; selEnd: number; text: string; isDynamic: boolean } | null {
  const tokenRe = /\{\{[^{}]+\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = tokenRe.exec(value)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (selStart >= start && selEnd <= end) {
      return { selStart: start, selEnd: end, text: match[0], isDynamic: true };
    }
  }

  if (selEnd > selStart) {
    const text = value.slice(selStart, selEnd);
    if (!text.includes('{{') && !text.includes('}}')) {
      return { selStart, selEnd, text, isDynamic: false };
    }
  }
  return null;
}

interface RequestMappingStepProps {
  watch: any;
  setValue: any;
  register: any;
  errors: any;
  canEdit: boolean;
  clearErrors: any;
}

export default function RequestMappingStep({
  watch,
  setValue,
  register,
  errors,
  canEdit,
  clearErrors,
}: RequestMappingStepProps) {
  type SectionKey = 'query' | 'body' | 'headers';

  const connectors = useConnectionsStore(s => s.connectors);
  const mainConnectionId = watch('mainConnectionId');
  const selectedConnector = connectors.find(c => c.id === mainConnectionId);
  const isMssqlConnector = selectedConnector?.connectionTypeId === 7;
  const httpMethod = watch('httpMethod') || 'POST';
  const isSelect = httpMethod === 'SELECT' || isMssqlConnector;

  const { mappings, addMapping, updateMapping, removeMapping, setMappingOriginalValue, getOriginalValue, syncFromTemplate } =
    useMappingBuilder({ watch, setValue, isSql: isSelect });

  const {
    queryMap, showJson: showQueryJson,
    jsonText: queryJsonText, addParam, updateParam, removeParam,
    setFromJson: setQueryFromJson, setQueryMap, syncQueryMapFromPath,
  } = useQueryMapBuilder({ watch, setValue });

  // Track selection/cursor in query param value inputs for Make Dynamic / Make Static
  const [queryValueSel, setQueryValueSel] = useState<{
    idx: number;
    selStart: number;
    selEnd: number;
    text: string;
    isDynamic: boolean;
    tooltipLeft: number;
  } | null>(null);

  const measureTooltipLeft = (input: HTMLInputElement, charIndex: number): number => {
    const style = getComputedStyle(input);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 0;
    ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const textBefore = input.value.slice(0, charIndex);
    const textWidth = ctx.measureText(textBefore).width;
    const paddingLeft = parseFloat(style.paddingLeft);
    return Math.max(0, paddingLeft + textWidth - input.scrollLeft);
  };

  const handleValueInputInteract = (idx: number, e: React.SyntheticEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const sel = resolveDynamicSelection(input.value, input.selectionStart ?? 0, input.selectionEnd ?? 0);
    if (!sel) {
      setQueryValueSel(null);
      return;
    }
    setQueryValueSel({ idx, ...sel, tooltipLeft: measureTooltipLeft(input, sel.selStart) });
  };

  // Track selection/cursor in the path input for Make Dynamic / Make Static
  const pathWrapperRef = useRef<HTMLDivElement>(null);
  const pathInputRef = useRef<HTMLInputElement>(null);

  const [pathSel, setPathSel] = useState<{
    selStart: number;
    selEnd: number;
    text: string;
    isDynamic: boolean;
    tooltipLeft: number;
    tooltipTop: number;
  } | null>(null);

  const handlePathInputInteract = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const wrapper = pathWrapperRef.current;
    const selStart = input.selectionStart ?? 0;
    const selEnd = input.selectionEnd ?? 0;
    const value = input.value;

    const computePos = (charIdx: number) => {
      const left = measureTooltipLeft(input, charIdx);
      let top = 0;
      if (wrapper) {
        const inputRect = input.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        top = inputRect.top - wrapperRect.top - 30;
      }
      return { tooltipLeft: left, tooltipTop: top };
    };

    const sel = resolveDynamicSelection(value, selStart, selEnd);
    if (!sel) {
      setPathSel(null);
      return;
    }
    setPathSel({ ...sel, ...computePos(sel.selStart) });
  };

  const handleTogglePathDynamic = () => {
    if (!pathSel) return;
    const { selStart, selEnd, text, isDynamic } = pathSel;
    const currentPath = (watch('path') as string) || '';
    let newPath: string;
    if (isDynamic) {
      const inner = text.replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '');
      newPath = currentPath.slice(0, selStart) + inner + currentPath.slice(selEnd);
    } else {
      const sanitized = text.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_') || 'value';
      newPath = currentPath.slice(0, selStart) + `{{${sanitized}}}` + currentPath.slice(selEnd);
    }
    setValue('path', newPath, { shouldValidate: true, shouldDirty: true });

    setPathSel(null);
  };

  const handlePathChange = (value: string) => {
    setValue('path', value, { shouldValidate: true, shouldDirty: true });
    syncQueryMapFromPath(value);
    setPathSel(null);
  };

  const handlePathPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    window.setTimeout(() => {
      handlePathChange(e.currentTarget.value);
    }, 0);
  };

  const handleToggleParamDynamic = (idx: number) => {
    if (!queryValueSel || queryValueSel.idx !== idx) return;
    const { selStart, selEnd, text, isDynamic } = queryValueSel;
    const currentValue = queryMap[idx].value;
    let newValue: string;
    let newDynamic: boolean;
    if (isDynamic) {
      // Strip {{ }} to make static
      const inner = text.replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '');
      newValue = currentValue.slice(0, selStart) + inner + currentValue.slice(selEnd);
      newDynamic = /\{\{[^{}]+\}\}/.test(newValue);
    } else {
      const sanitized = text.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_') || 'value';
      newValue = currentValue.slice(0, selStart) + `{{${sanitized}}}` + currentValue.slice(selEnd);
      newDynamic = true;
    }
    const updated = queryMap.map((q, i) =>
      i === idx ? { ...q, value: newValue, dynamic: newDynamic } : q
    );
    setValue('queryMap', updated, { shouldDirty: true });
    setQueryValueSel(null);
  };

  const { pairs: headerPairs, updatePair: updateHeader, addPair: addHeader, removePair: removeHeader, hydrateFromJson: hydrateHeaders } =
    useHeaderBuilder({ watch, setValue, clearErrors });

  const [showRawTemplate, setShowRawTemplate] = useState(() => { return mappings.length === 0 });
  const [aiLoadingMapping, setAiLoadingMapping] = useState(false);
  const [sqlDbLoading, setSqlDbLoading] = useState(false);

  const handleGenerateSqlFromDb = async () => {
    if (!mainConnectionId) return;
    setSqlDbLoading(true);
    try {
      const toolDescription = watch('description') || '';
      const dbSchema = await configApi.getDbSchema(mainConnectionId);
      const result: any = await aiApi.generateContent({
        type: AiGenerationTypes.sqlQuery,
        description: toolDescription,
        context: { dbSchema, inputSchema: inputSchemaRaw },
      });
      const sqlText = typeof result === 'string' ? result : (result?.content ?? JSON.stringify(result));
      setValue('bodyTemplate', sqlText.trim(), { shouldDirty: true });
      syncFromTemplate(sqlText.trim());
      toast.success('SQL generated from DB schema!');
    } catch (error: any) {
      toast.error(`Failed to generate SQL: ${error.message}`);
    } finally {
      setSqlDbLoading(false);
    }
  };
  const [showHeadersJson, setShowHeadersJson] = useState(false);
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    query: true,
    body: true,
    headers: true,
  });

  const toggleSection = (section: SectionKey) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Get available fields from inputSchema
  const inputSchemaRaw = watch('inputSchema') || '{}';
  const availableFields: VisualField[] = (() => {
    const parsed = safeJsonParse(inputSchemaRaw, {});
    if (parsed.success && parsed.data) {
      return schemaToFields(parsed.data);
    }
    return [];
  })();

  const isGet = httpMethod === 'GET';

  const mappedBodyFieldNames = new Set(
    mappings.filter(m => !m.isQueryParam && m.sourceFieldName).map(m => m.sourceFieldName)
  );
  const allFieldsMappedToBody =
    availableFields.length > 0 &&
    availableFields.every(f => mappedBodyFieldNames.has(f.name));
  const bodyTemplate = watch('bodyTemplate') || '';

  const handleCreateField = (fieldName: string) => {
    const parsed = safeJsonParse(inputSchemaRaw, {});
    const currentFields = parsed.success && parsed.data ? schemaToFields(parsed.data) : [];
    if (currentFields.some(f => f.name === fieldName)) return;
    const newField = {
      id: crypto.randomUUID(),
      name: fieldName,
      label: fieldName,
      type: 'string' as const,
      description: '',
      required: false,
      example: '',
    };
    setValue('inputSchema', JSON.stringify(fieldsToSchema([...currentFields, newField]), null, 2), { shouldDirty: true });
  };

  // AI auto-mapping handler
  const handleAiMappingResult = (value: string) => {
    if (isGet) {
      try {
        const parsed = JSON.parse(value);
        const newQueryMap = parsed.queryMap || parsed;
        if (Array.isArray(newQueryMap)) {
          const normalized = newQueryMap.map((item: any) => ({
            key: item.key || '',
            value: item.value || '',
            selected: item.selected !== undefined ? item.selected : true,
            dynamic: item.dynamic !== undefined ? item.dynamic : false,
          }));
          setQueryMap(normalized);
          syncFromTemplate((watch('bodyTemplate') as string) || '');
        }
      } catch {
        setQueryMap([]);
        syncFromTemplate((watch('bodyTemplate') as string) || '');
      }
    } else {
      setValue('bodyTemplate', value, { shouldDirty: true });
      syncFromTemplate(value);
    }
  };

  return (
    <div className={styles.step}>
      {!isSelect &&
        <div className={styles.methodRow}>
          <div className={styles.methodField}>
            <div className={styles.labelRow}>
              <label className={styles.label}>{'HTTP Method'}</label>
              <InfoLabel content={'Use GET when the request is fully expressed in the URL. Use POST when the tool sends a request body.'} />
            </div>
            <select
              {...register('httpMethod')}
              className={styles.select}
              disabled={!canEdit || isSelect}
            >
              <>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </>
            </select>
          </div>
        </div>}

      {!isSelect && (
        <div className={styles.pathField}>
          <div ref={pathWrapperRef} className={styles.pathInputWrapper}>
            <SecureInput
              ref={pathInputRef}
              fieldType="text"
              preserveWhitespace
              label="Endpoint Path"
              description="Start with the endpoint path. Selected query parameters are appended to this URL automatically."
              value={watch('path') || ''}
              onChange={handlePathChange}
              onPaste={handlePathPaste}
              onMouseUp={handlePathInputInteract}
              onKeyUp={handlePathInputInteract}
              onBlur={() => setTimeout(() => setPathSel(null), 150)}
              placeholder="/api/endpoint"
              disabled={!canEdit}
              required
              className={styles.input}
              externalError={errors.path?.message}
            />
            {canEdit && pathSel && (
              <button
                type="button"
                className={`${styles.dynamicTooltip} ${pathSel.isDynamic ? styles.makeStatic : styles.makeDynamic}`}
                style={{ top: pathSel.tooltipTop, left: pathSel.tooltipLeft, bottom: 'auto' }}
                onMouseDown={e => e.preventDefault()}
                onClick={handleTogglePathDynamic}
                title={pathSel.isDynamic ? 'Remove {{ }} and make static' : 'Wrap selected text as a dynamic {{ variable }}'}
              >
                <Zap size={11} />
                {pathSel.isDynamic ? 'Make Static' : 'Make Dynamic'}
              </button>
            )}
          </div>
          {(() => {
            const pathParams = extractPathParams(watch('path') || '');
            if (pathParams.length === 0) return null;
            const fieldNames = availableFields.map(f => f.name);
            const missing = pathParams.filter(p => !fieldNames.includes(p));
            return (
              <div className={styles.pathParamsHint}>
                <span>Path parameters: </span>
                {pathParams.map(p => (
                  <span key={p} className={missing.includes(p) ? styles.pathParamTagWarning : styles.pathParamTag}>{p}</span>
                ))}
                {missing.length > 0 && (
                  <span className={styles.pathParamWarning}>
                    <AlertTriangle size={12} />
                    {missing.join(', ')} not defined in input fields
                  </span>
                )}
              </div>
            );
          })()}
          {(() => {
            const dynamicVars = [...new Set(
              queryMap.flatMap(q => {
                const matches = [...(q.value || '').matchAll(/\{\{\s*([^}|]+?)\s*(?:\|[^}]*)?\}\}/g)];
                return matches.map(m => m[1].trim());
              })
            )];
            if (dynamicVars.length === 0) return null;
            const fieldNames = availableFields.map(f => f.name);
            const missing = dynamicVars.filter(v => !fieldNames.includes(v));
            return (
              <div className={styles.pathParamsHint}>
                <span>Query parameters: </span>
                {dynamicVars.map(v => (
                  <span key={v} className={missing.includes(v) ? styles.pathParamTagWarning : styles.pathParamTag}>{v}</span>
                ))}
                {missing.length > 0 && (
                  <span className={styles.pathParamWarning}>
                    <AlertTriangle size={12} />
                    {missing.join(', ')} not defined in input fields
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      )}
      {
        !isSelect && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionHeading}>
                <Globe size={16} />
                <h3 className={styles.sectionTitle}>Query Parameters</h3>
                <InfoLabel
                  content={isGet ? 'Define the parameters that are sent in the URL.' : 'Add optional URL query parameters for this request.'}
                />
              </div>

              <button
                type="button"
                className={styles.sectionToggle}
                onClick={() => toggleSection('query')}
                aria-expanded={openSections.query}
                aria-label={openSections.query ? 'Collapse query parameters' : 'Expand query parameters'}
              >
                {openSections.query ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
            {showQueryJson && (
              <div className={styles.advancedInner}>
                <TemplateEditor
                  value={queryJsonText}
                  onChange={setQueryFromJson}
                  height="180px"
                  title="Query Parameters JSON"
                  readOnly={!canEdit}
                />
              </div>
            )}
            {openSections.query && (
              <div className={styles.mappingList}>
                <div className={styles.queryHeaders}>
                  <span className={styles.colHeaderCheck} />
                  <span className={styles.colHeader}>Key</span>
                  <span className={styles.colHeader}>Value</span>
                  <span className={styles.colHeaderAction} />
                </div>
                {queryMap.map((param, idx) => (
                  <div key={idx} className={styles.queryRow}>
                    <input
                      type="checkbox"
                      checked={param.selected || false}
                      onChange={() => updateParam(idx, 'selected', !param.selected)}
                      disabled={!canEdit}
                      className={styles.queryCheckbox}
                    />
                    <input
                      type="text"
                      className={styles.queryInput}
                      value={param.key || ''}
                      onChange={e => updateParam(idx, 'key', e.target.value)}
                      placeholder="Parameter name"
                      disabled={!canEdit}
                    />
                    <div className={styles.queryValueWrapper}>
                      <input
                        type="text"
                        className={styles.queryInput}
                        value={param.value || ''}
                        onChange={e => {
                          updateParam(idx, 'value', e.target.value);
                          setQueryValueSel(null);
                        }}
                        onMouseUp={e => handleValueInputInteract(idx, e)}
                        onKeyUp={e => handleValueInputInteract(idx, e)}
                        onBlur={() => setTimeout(() => setQueryValueSel(null), 150)}
                        placeholder="Value or {{field}}"
                        disabled={!canEdit}
                      />
                      {canEdit && queryValueSel?.idx === idx && (
                        <button
                          type="button"
                          className={`${styles.dynamicTooltip} ${queryValueSel.isDynamic ? styles.makeStatic : styles.makeDynamic}`}
                          style={{ left: queryValueSel.tooltipLeft }}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => handleToggleParamDynamic(idx)}
                          title={queryValueSel.isDynamic ? 'Remove {{ }} and make static' : 'Wrap selected text as a dynamic {{ variable }}'}
                        >
                          <Zap size={11} />
                          {queryValueSel.isDynamic ? 'Make Static' : 'Make Dynamic'}
                        </button>
                      )}
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        className={styles.queryDeleteBtn}
                        onClick={() => removeParam(idx)}
                        aria-label="Remove parameter"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <button
                    type="button"
                    className={styles.addBtn}
                    onClick={addParam}
                  >
                    <Plus size={14} />
                    Add Parameter
                  </button>
                )}
              </div>
            )}
          </div>
        )
      }

      {/* Body Mapping (POST/PUT/PATCH) or SQL Query (SELECT) */}
      {
        (!isGet || isSelect) && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionHeading}>
                {isSelect ? <Database size={16} /> : <FileJson size={16} />}
                <h3 className={styles.sectionTitle}>{isSelect ? 'SQL Query' : 'Request Body'}</h3>
                <InfoLabel content={isSelect
                  ? 'Write a SELECT query using {{ fieldName }} for dynamic parameters. Values are safely parameterized to prevent SQL injection.'
                  : 'Map dynamic input fields to the request body. Use the raw body template when you need static values or custom Jinja logic.'
                } />
              </div>
              <button
                type="button"
                className={styles.sectionToggle}
                onClick={() => toggleSection('body')}
                aria-expanded={openSections.body}
                aria-label={openSections.body ? 'Collapse' : 'Expand'}
              >
                {openSections.body ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
            {openSections.body && (
              <>
                {isSelect ? (
                  <SqlQuerySection
                    mappings={mappings}
                    availableFields={availableFields}
                    canEdit={canEdit}
                    onUpdateMapping={updateMapping}
                    onRemoveMapping={removeMapping}
                    onGenerateSqlFromDb={handleGenerateSqlFromDb}
                    sqlDbLoading={sqlDbLoading}
                    mainConnectionId={mainConnectionId}
                    bodyTemplate={watch('bodyTemplate')}
                    onBodyTemplateChange={(value) => {
                      setValue('bodyTemplate', value, { shouldDirty: true });
                      syncFromTemplate(value);
                    }}
                    onMakeDynamic={(info) => setMappingOriginalValue(info.variableName, info.originalValue)}
                    getOriginalValue={getOriginalValue}
                    onMakeUndynamic={(newTemplate) => setValue('bodyTemplate', newTemplate, { shouldDirty: true })}
                    inputSchemaRaw={inputSchemaRaw}
                    description={watch('description') || ''}
                    aiLoadingMapping={aiLoadingMapping}
                    setAiLoadingMapping={setAiLoadingMapping}
                    onAiMappingResult={handleAiMappingResult}
                  />
                ) : (
                  <>
                    <button
                      type="button"
                      className={styles.collapseBtn}
                      onClick={() => setShowRawTemplate(!showRawTemplate)}
                    >
                      {showRawTemplate ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      Raw Body Template
                    </button>

                    {showRawTemplate && (
                      <div className={styles.advancedInner}>
                        <TemplateEditor
                          value={watch('bodyTemplate')}
                          onChange={(value) => {
                            setValue('bodyTemplate', value, { shouldDirty: true });
                            syncFromTemplate(value);
                          }}
                          onMakeDynamic={(info) => {
                            setTimeout(() => {
                              setMappingOriginalValue(info.variableName, info.originalValue);
                            }, 0);
                          }}
                          getOriginalValue={getOriginalValue}
                          onMakeUndynamic={(newTemplate) => {
                            setValue('bodyTemplate', newTemplate, { shouldDirty: true });
                          }}
                          height="250px"
                          title="Request Body Template"
                          templateType="request"
                          placeholder="Enter Jinja2 template for request body..."
                          readOnly={!canEdit}
                        />
                      </div>
                    )}

                    <div className={styles.mappingList}>
                      {mappings.length > 0 && (
                        <div className={styles.mappingHeaders}>
                          <span className={styles.colHeader}>Body Path</span>
                          <span className={styles.colHeaderArrow} />
                          <span className={styles.colHeader}>Input Field</span>
                        </div>
                      )}
                      {mappings.map(mapping => (
                        <MappingRow
                          key={mapping.id}
                          mapping={mapping}
                          availableFields={availableFields}
                          canEdit={canEdit}
                          onChange={(updates) => updateMapping(mapping.id, updates)}
                          onRemove={() => removeMapping(mapping.id)}
                          onCreateField={handleCreateField}
                        />
                      ))}
                      {canEdit && (
                        <button
                          type="button"
                          className={styles.addBtn}
                          onClick={() => addMapping()}
                        >
                          <Plus size={14} />
                          Add Body Mapping
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            {/* AI auto-mapping - non-SELECT only */}
            {canEdit && !isSelect && availableFields.length > 0 && (
              <div className={styles.aiSection}>
                <AiGenerator
                  icon={<Sparkles size={16} />}
                  buttonText="Auto-generate body from input fields"
                  disabled={allFieldsMappedToBody}
                  prompt={`Generate ${isGet ? 'query parameters' : 'request body template'} for input fields: ${inputSchemaRaw}`}
                  setContent={handleAiMappingResult}
                  loading={aiLoadingMapping}
                  setAiLoading={setAiLoadingMapping}
                  previousData={isGet ? JSON.stringify(queryMap) : bodyTemplate}
                  generateBody={{
                    type: AiGenerationTypes.schemaToJson,
                    description: '',
                    context: {
                      schema: inputSchemaRaw,
                      referenceJson: isGet ? { queryMap } : bodyTemplate,
                    },
                  }}
                />
              </div>
            )}
          </div>
        )
      }

      {/* Headers (hidden for SELECT mode) */}
      {
        !isSelect && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionHeading}>
                <ListPlus size={16} />
                <h3 className={styles.sectionTitle}>Headers</h3>
                <InfoLabel content="Define custom headers to include in the request. In advanced mode you can edit the raw JSON directly." />
              </div>

              <button
                type="button"
                className={styles.sectionToggle}
                onClick={() => toggleSection('headers')}
                aria-expanded={openSections.headers}
                aria-label={openSections.headers ? 'Collapse headers' : 'Expand headers'}
              >
                {openSections.headers ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
            <button
              type="button"
              className={styles.collapseBtn}
              onClick={() => setShowHeadersJson(!showHeadersJson)}
            >
              {showHeadersJson ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              Raw JSON
            </button>
            {showHeadersJson && (
              <div className={styles.advancedInner}>
                <TemplateEditor
                  letDynamic={false}
                  value={watch('headerMap') || '{}'}
                  onChange={(val) => {
                    clearErrors('headerMap');
                    setValue('headerMap', val, { shouldDirty: true });
                    hydrateHeaders(val);
                  }}
                  height="180px"
                  title="Headers JSON"
                  readOnly={!canEdit}
                />
              </div>
            )}
            {openSections.headers && (
              <div className={styles.headersContent}>
                {headerPairs.map((pair, idx) => (
                  <div key={idx} className={styles.headerRow}>
                    <input
                      type="text"
                      className={styles.headerInput}
                      value={pair.key}
                      onChange={e => updateHeader(idx, 'key', e.target.value)}
                      placeholder="Header name"
                      disabled={!canEdit}
                    />
                    <input
                      type="text"
                      className={styles.headerInput}
                      value={pair.value}
                      onChange={e => updateHeader(idx, 'value', e.target.value)}
                      placeholder="Value"
                      disabled={!canEdit}
                    />
                    {canEdit && (
                      <button
                        type="button"
                        className={styles.headerRemoveBtn}
                        onClick={() => removeHeader(idx)}
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <button
                    type="button"
                    className={styles.addBtn}
                    onClick={addHeader}
                  >
                    <Plus size={14} />
                    Add Header
                  </button>
                )}
              </div>
            )}
          </div>
        )
      }
    </div >
  );
}
